<script setup lang="ts">
import { EDataUnit } from "@/constants/data-units.type";
import { MetricUnit } from "@/domains/v4b/site-consumption/site-consumption.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { getNumberWithSign } from "@/utils/number";
import { formatWhValue } from "@/utils/translation-utils";

import Skeleton from "primevue/skeleton";

import DashboardCard from "@/components/data/dashboard/dashboard-card.vue";

export interface ITopFlopItem {
  value: number;
  unit: MetricUnit;
  siteName: string;
}

interface IProps {
  title: string;
  items?: ITopFlopItem[];
  isLoading?: boolean;
}

const props = defineProps<IProps>();

const { t, k } = useTypedIntl();

function formatValue(item: ITopFlopItem): string {
  switch (item.unit) {
    case EDataUnit.WATT_HOUR:
      return formatWhValue(item.value, 0);
    case EDataUnit.PERCENT:
      return `${t(k.global.units[EDataUnit.PERCENT]({ value: getNumberWithSign(item.value) }))}`;
    default:
      return item.value.toString();
  }
}
</script>

<template>
  <DashboardCard :title="props.title">
    <template #title-icon>
      <slot name="title-icon">
        <div class="bg-gray-100 rounded-full !w-[20px] !h-[20px] flex justify-center items-center">
          <i class="pi pi-chart" />
        </div>
      </slot>
    </template>

    <Skeleton
      v-if="props.isLoading"
      class="!h-32"
    />

    <div
      v-else-if="props.items?.length"
      class="flex flex-col gap-2"
    >
      <div
        v-for="(item, _index) in props.items"
        :key="_index"
        class="flex flex-row items-center gap-2"
      >
        <small
          class="rounded-full py-1 px-2 font-semibold"
          :class="{
            'bg-red-200 text-red-700': item.unit === EDataUnit.PERCENT && item.value > 0,
            'bg-green-200 text-green-700': item.unit === EDataUnit.PERCENT && item.value < 0,
            'bg-blue-200 text-blue-700': item.unit === EDataUnit.WATT_HOUR,
          }"
        >
          {{ formatValue(item) }}
        </small>
        <span class="flex-1 font-semibold">
          {{ item.siteName }}
        </span>
      </div>
    </div>

    <div
      v-else
      class="flex justify-center w-full h-full min-h-16 items-center"
    >
      <span class="text-text-secondary">
        {{ t(k.global.misc.noResult) }}
      </span>
    </div>
  </DashboardCard>
</template>
